import {
    Grid,
    useStyles,
    MemberTile,
    Flex,
    Button,
} from '@archipro-design/aria';
import type { UIMatch } from '@remix-run/react';
import { useFetcher, useMatches, useSearchParams } from '@remix-run/react';
import PageSection from '@modules/professional/component/page-layout/PageSection';
import * as S from './TeamMembers.style';

import React, { useState, useRef } from 'react';

import type { TeamMembersResult } from './TeamMembers';
import { getMemberPlaceholderImage } from './TeamMembers';
import type { TeamMemberView } from '@modules/professional/type';
import { TeamMemberDetailsModal } from '@modules/professional/component/about-tab/team-member/TeamMemberDetailsModal';
import { ClientOnly } from 'remix-utils';
import { useMemoizedFn } from 'ahooks';
import {
    INFO_PAGE_MATCH_ID,
    INFO_PAGE_MATCH_ID_EDITMODE,
    PAGE_ANCHORS,
} from '@modules/professional/config/constants';
import { useTracker } from '@archipro-website/tracker';
import { useConfigContext } from '@archipro-website/config/bindings/react';

import { useScrollToHandle } from '@modules/root/hook/use-scrollto-handle';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useFeature } from '~/modules/root';
import TeamMemberDetailsModalUpdated from './TeamMemberDetailsModalUpdated';
import type { APHandle } from '~/modules/root';
import type { ProfessionalTeamMemberDetailsLoaderData } from '~/modules/professional/page/TeamMemberDetailsPage';
import type { InfoPageLoaderData } from '~/modules/professional/api/info-page-loader.server';

export const TeamMembersDesktop: React.FC<
    TeamMembersResult & { sectionTitle: string }
> = ({ teamMembers, pageInfo, sectionTitle, disablePhotoViewer }) => {
    const anchorRef = useRef<HTMLDivElement>(null);
    const { css } = useStyles();
    const matches = useMatches();
    const routeMatch = matches[matches.length - 1] as UIMatch<
        ProfessionalTeamMemberDetailsLoaderData,
        APHandle
    >;

    const tracker = useTracker();
    const teamMemberFetcher = useFetcher<InfoPageLoaderData>();
    const [searchParams, setSearchParams] = useSearchParams();

    const isDialogRoute = routeMatch?.handle?.isDialogRoute;
    const memberProfileID = routeMatch?.data?.memberID;
    const hasNextPage =
        teamMemberFetcher?.data?.teamMembersResult?.pageInfo.hasNextPage ??
        teamMembers.length < pageInfo.totalCount;

    useScrollToHandle(PAGE_ANCHORS.TEAM_MEMBERS, anchorRef.current);

    const infoPagePath =
        matches.find((match) =>
            [INFO_PAGE_MATCH_ID, INFO_PAGE_MATCH_ID_EDITMODE].includes(match.id)
        )?.pathname ?? '/';

    const fetcherLoading = teamMemberFetcher.state === 'submitting';

    const loadMoreTeamMembers = () => {
        if (fetcherLoading) return;

        tracker.log('ProfessionalAllTeamClick', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
        });

        setSearchParams(
            {
                ...searchParams,
                teamPage: ((pageInfo?.currentPage ?? 1) + 1).toString(),
            },
            {
                preventScrollReset: true,
            }
        );
    };

    return (
        <div
            className={css(hasNextPage ? S.TeamMemberWrapper : {})}
            ref={anchorRef}
        >
            <PageSection sectionTitle={sectionTitle}>
                <Grid columns={5} className={css(S.TeamMemberGrid)}>
                    {teamMembers.map((member) => (
                        <MemberDesktop
                            key={member.id}
                            member={member}
                            basePath={infoPagePath}
                            modalIsOpen={
                                isDialogRoute && member.id === memberProfileID
                            }
                            disablePhotoViewer={disablePhotoViewer}
                        />
                    ))}
                </Grid>
            </PageSection>

            {hasNextPage && (
                <Flex hAlign="start">
                    <Button
                        onClick={loadMoreTeamMembers}
                        size={16}
                        outlined={true}
                        className={css(S.LoadMoreButton)}
                    >
                        {!fetcherLoading ? `View more` : 'Loading...'}
                    </Button>
                </Flex>
            )}
        </div>
    );
};

const MemberDesktop: React.FC<{
    member: TeamMemberView;
    modalIsOpen?: boolean;
    disablePhotoViewer?: boolean;
    basePath: string;
}> = ({ member, modalIsOpen = false, disablePhotoViewer }) => {
    const { detailsPageImageSrc, name, jobTitle } = member;
    const [modalOpen, setModalOpen] = useState(modalIsOpen);
    const tracker = useTracker();
    const config = useConfigContext();

    const imageProps = {
        src: detailsPageImageSrc,
        width: 344,
        height: 413,
        unoptimized:
            detailsPageImageSrc ===
            getMemberPlaceholderImage(config.assetsBaseUrl)
                ? true
                : undefined,
    };

    const jobTitleProps = {
        maximumLines: 1,
        children: jobTitle ?? '',
    };

    const handleToggleDetails = useMemoizedFn(
        (event: React.SyntheticEvent<HTMLElement>) => {
            event.stopPropagation();
            if (!disablePhotoViewer) {
                setModalOpen(true);
            }
        }
    );

    const teamMemberNextEnabled = useFeature('team_member_next');

    return (
        <>
            <ClientOnly fallback={null}>
                {() =>
                    teamMemberNextEnabled ? (
                        <TeamMemberDetailsModalUpdated
                            member={member}
                            website={member.website ?? ''}
                            toggleModal={() => {
                                setModalOpen(false);
                            }}
                            isOpen={modalOpen}
                        />
                    ) : (
                        <TeamMemberDetailsModal
                            member={member}
                            toggleModal={() => {
                                setModalOpen(false);
                            }}
                            website={member.website ?? ''}
                            isOpen={modalOpen}
                        />
                    )
                }
            </ClientOnly>

            <MemberTile
                image={{
                    ...imageProps,
                    alt: name,
                    width: 348,
                    height: 420,
                    layout: 'fill',
                    sizes: getImagesSizes({
                        desktop: 348,
                        mobile: 174,
                    }),
                }}
                name={name}
                jobTitle={jobTitleProps}
                onToggleDetails={handleToggleDetails}
                onClick={() => {
                    tracker.log('ProfessionalExpandTeamClick', {
                        url: new URL(window.location.href),
                        targetTracker: 'archiproTracker',
                    });
                }}
            />
        </>
    );
};
